import * as React from 'react'

import { SearchIcon, XIcon } from 'lucide-react'
import { observer } from 'mobx-react'
import { classList, toggleClass } from 'react-classlist-helper'

import { Button } from '~/client/src/shared/components/shadcn-ui/button'
import Spinner from '~/client/src/shared/components/Spinner/Spinner'
import { NOOP } from '~/client/src/shared/utils/noop'
import { notifyOverlayOpen } from '~/client/src/shared/utils/overlayNotifier'

import { EMPTY_STRING } from '../../utils/usefulStrings'
import Dimmer from '../Dimmer'

import './BaseCompactPopup.scss'

interface IProps {
  title?: string
  subtitle?: string
  isShown: boolean
  onHide: (event?: React.SyntheticEvent<HTMLElement>) => void
  actionButtonTitle?: string
  isActionButtonEnabled?: boolean
  customLeftIcon?: JSX.Element
  onCustomLeftIconClick?: () => void
  onActionButtonClick?: () => void

  containerClassName?: string
  titleClassName?: string
  childrenClassName?: string

  shouldIncludeSearch?: boolean
  searchPlaceholder?: string
  searchValue?: string
  onSearchValueChange?: (newValue: string) => void
  onSearchEnterPress?: (event: React.KeyboardEvent<HTMLInputElement>) => void
  onSearchValueClear?: () => void

  isLoading?: boolean
}

@observer
export default class BaseCompactPopup extends React.Component<IProps> {
  private closeOverlay: (() => void) | null = null

  public componentDidMount() {
    if (this.props.isShown) {
      this.closeOverlay = notifyOverlayOpen()
    }
  }

  public componentDidUpdate(prevProps: IProps) {
    if (!prevProps.isShown && this.props.isShown) {
      this.closeOverlay = notifyOverlayOpen()
    } else if (prevProps.isShown && !this.props.isShown) {
      this.closeOverlay?.()
      this.closeOverlay = null
    }
  }

  public componentWillUnmount() {
    this.closeOverlay?.()
  }

  public static defaultProps = {
    onActionButtonClick: NOOP,
    onSearchValueChange: NOOP,
    onSearchEnterPress: NOOP,
    onSearchValueClear: NOOP,

    titleClassName: 'text center large bold',
  }

  public render() {
    const {
      isShown,
      title,
      subtitle,
      onHide,
      children,
      titleClassName,
      actionButtonTitle,
      isActionButtonEnabled,
      onActionButtonClick,
      childrenClassName,
      shouldIncludeSearch,
      searchPlaceholder,
      searchValue,
      containerClassName,
    } = this.props

    return (
      <>
        {isShown && <Dimmer onClick={onHide} shown={isShown} />}
        <div
          className={classList({
            'compact-popup-modal col tw:border-t tw:border-border': true,
            shown: isShown,
            [containerClassName]: !!containerClassName,
          })}
        >
          {!!title && (
            // shrink-0 because the popup is a fixed-height flex column whose
            // content pane asks for height:100% — without it the header is
            // compressed below its own content and a wrapped title is clipped.
            <div className="tw:flex tw:shrink-0 tw:items-center tw:gap-2 tw:px-2.5 tw:py-3">
              {/* Equal side slots keep a short title centred and give a long one
                  the full width between the buttons before it wraps. */}
              <div className="tw:flex tw:flex-1">{this.renderIcon()}</div>
              <div className="tw:flex tw:min-w-0 tw:flex-col tw:items-center">
                <div
                  className={`${titleClassName} tw:max-w-full tw:break-words`}
                >
                  {title}
                </div>
                {!!subtitle && (
                  <div className="text light tw:max-w-full tw:break-words">
                    {subtitle}
                  </div>
                )}
              </div>
              <div className="tw:flex tw:flex-1 tw:justify-end">
                {actionButtonTitle && (
                  <Button
                    onClick={onActionButtonClick}
                    disabled={!isActionButtonEnabled || this.props.isLoading}
                    variant="default"
                  >
                    {this.props.isLoading ? (
                      <Spinner size="xs" />
                    ) : (
                      actionButtonTitle
                    )}
                  </Button>
                )}
              </div>
            </div>
          )}
          {shouldIncludeSearch && (
            <div className="row search-input bg-palette-brand-lightest px16 by-light-input-border">
              <SearchIcon
                size={16}
                className={classList({
                  mr15: true,
                  'text blue': !!searchValue,
                })}
              />
              <input
                type="text"
                onChange={this.onSearchValueChange}
                onKeyPress={this.handleEnterPress}
                className="full-width full-height bare-input no-outline"
                placeholder={searchPlaceholder}
                value={searchValue || EMPTY_STRING}
              />
              <XIcon
                size={16}
                onClick={this.clearSearchValue}
                className={toggleClass('transparent', !searchValue)}
              />
            </div>
          )}
          <div
            className={classList({
              'overflow-y-auto': true,
              [childrenClassName]: !!childrenClassName,
            })}
          >
            {children}
          </div>
        </div>
      </>
    )
  }

  private renderIcon(): JSX.Element {
    const { customLeftIcon, onCustomLeftIconClick, onHide } = this.props

    return (
      <Button
        variant="ghost"
        size="icon-sm"
        aria-label={customLeftIcon ? 'Back' : 'Close'}
        onClick={customLeftIcon ? (onCustomLeftIconClick ?? onHide) : onHide}
      >
        {customLeftIcon || <XIcon />}
      </Button>
    )
  }

  private onSearchValueChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    this.props.onSearchValueChange(event.target.value)
  }

  private handleEnterPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    this.props.onSearchEnterPress(event)
  }

  private clearSearchValue = () => {
    this.props.onSearchValueClear()
  }
}
